import type { CSSProperties } from 'react';
import { cn } from '@/lib/cn';
import { flavorVar, type Flavor } from './flavors';

interface BlobProps {
  flavor?: Flavor;
  /** Diameter. Any CSS length; number is treated as px. Default '20rem'. */
  size?: number | string;
  /** Slow float + rotate loop (frozen under reduced motion). Default true. */
  float?: boolean;
  /** Offsets the float loop so a cluster of blobs drifts out of sync. */
  delay?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Purely decorative gradient orb with an organic squircle shape. Always
 * `aria-hidden`. Drop a few behind a hero or section for depth.
 *
 *   <Blob flavor="berry" size={360} className="absolute -left-24 top-10" />
 */
export function Blob({
  flavor = 'berry',
  size = '20rem',
  float = true,
  delay = 0,
  className,
  style,
}: BlobProps) {
  const dim = typeof size === 'number' ? `${size}px` : size;
  return (
    <span
      aria-hidden
      className={cn('rounded-blob pointer-events-none block', float && 'float-slow', className)}
      style={{
        width: dim,
        height: dim,
        backgroundImage: `radial-gradient(circle at 32% 28%, ${flavorVar(flavor)}, ${flavorVar(
          flavor,
          'soft',
        )} 72%)`,
        filter: 'blur(2px)',
        animationName: float ? 'float-slow' : undefined,
        animationDuration: '12s',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
        animationDelay: `${delay}s`,
        ...style,
      }}
    />
  );
}
